import { ChevronRight, type LucideIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import { cn } from "@/v2/lib/utils";
import { isDefaultFilters, nonDefaultFields } from "./defaults";
import {
	FIELD_DEF_BY_KEY,
	FIELD_DEFS,
	type FieldKey,
	type FieldOptionList,
} from "./fields";
import type { EntryFilterState, EnumFilterField } from "./types";
import { ValueChecklist } from "./ValueChecklist";

export type FieldOptions = Partial<Record<EnumFilterField, FieldOptionList>>;

export type FieldShortcut = {
	label: string;
	description?: string;
	active?: boolean;
	onSelect: () => void;
};

export interface FilterMenuProps {
	trigger: ReactNode;
	fieldOptions: FieldOptions;
	filters: EntryFilterState;
	defaultFilters?: EntryFilterState;
	fieldShortcuts?: Partial<Record<EnumFilterField, FieldShortcut[]>>;
	singleSelectFields?: Partial<Record<EnumFilterField, boolean>>;
	onToggleFieldValue: (field: EnumFilterField, value: string) => void;
	onSelectAll: (field: EnumFilterField) => void;
	onSelectNone: (field: EnumFilterField) => void;
	onReset: () => void;
}

const PANEL_BASE =
	"rounded-lg border border-border bg-popover text-popover-foreground shadow-md p-2";
const DEFAULT_PANEL_WIDTH = "w-56";

export function FilterMenu({
	trigger,
	fieldOptions,
	filters,
	defaultFilters,
	fieldShortcuts,
	singleSelectFields,
	onToggleFieldValue,
	onSelectAll,
	onSelectNone,
	onReset,
}: FilterMenuProps) {
	const [open, setOpen] = useState(false);
	const [activeField, setActiveField] = useState<FieldKey | null>(null);
	const dottedFields = nonDefaultFields(filters, defaultFilters);
	const canReset = !isDefaultFilters(filters, defaultFilters);

	useEffect(() => {
		if (!open) setActiveField(null);
	}, [open]);

	const selectedValuesFor = (
		field: EnumFilterField,
	): ReadonlyArray<string> | null => {
		if (field === "status") return filters.status;
		if (field === "timekeeper") return filters.timekeeper ?? null;
		return filters[field] ?? null;
	};

	const activeDef = activeField ? FIELD_DEF_BY_KEY[activeField] : null;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverContent
				align="start"
				sideOffset={8}
				className="flex w-auto items-start gap-2 border-0 bg-transparent p-0 shadow-none"
			>
				<div className={cn(PANEL_BASE, DEFAULT_PANEL_WIDTH)}>
					<FieldPane
						fieldOptions={fieldOptions}
						activeField={activeField}
						onSetActiveField={setActiveField}
						dottedFields={dottedFields}
						canReset={canReset}
						onReset={onReset}
					/>
				</div>
				{activeDef && (
					<div
						className={cn(
							PANEL_BASE,
							activeDef.valueWidthClass ?? DEFAULT_PANEL_WIDTH,
						)}
					>
						<ValueChecklist
							field={activeDef.key}
							options={fieldOptions[activeDef.key] ?? []}
							selectedValues={selectedValuesFor(activeDef.key)}
							singleSelect={singleSelectFields?.[activeDef.key]}
							shortcuts={fieldShortcuts?.[activeDef.key]}
							onToggle={(v) => onToggleFieldValue(activeDef.key, v)}
							onSelectAll={() => onSelectAll(activeDef.key)}
							onSelectNone={() => onSelectNone(activeDef.key)}
						/>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

function FieldPane({
	fieldOptions,
	activeField,
	onSetActiveField,
	dottedFields,
	canReset,
	onReset,
}: {
	fieldOptions: FieldOptions;
	activeField: FieldKey | null;
	onSetActiveField: (f: FieldKey | null) => void;
	dottedFields: ReadonlySet<FieldKey>;
	canReset: boolean;
	onReset: () => void;
}) {
	const [query, setQuery] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);
	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	const filtered = FIELD_DEFS.filter((d) => {
		const hasOptions = (fieldOptions[d.key]?.length ?? 0) > 0;
		if (!hasOptions) return false;
		return d.label.toLowerCase().includes(query.trim().toLowerCase());
	});

	return (
		<div className="flex flex-col">
			<Input
				ref={inputRef}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				placeholder="Add filter…"
				className="h-8 px-2 text-xs"
			/>
			<div className="mt-2 max-h-72 overflow-y-auto">
				{filtered.length === 0 ? (
					<div className="px-2 py-3 text-center text-xs text-muted-foreground">
						No matches
					</div>
				) : (
					filtered.map((def) => {
						const Icon: LucideIcon = def.icon;
						const isActive = activeField === def.key;
						return (
							<Button
								key={def.key}
								variant="ghost"
								size="sm"
								onMouseEnter={() => onSetActiveField(def.key)}
								onFocus={() => onSetActiveField(def.key)}
								onClick={() => onSetActiveField(def.key)}
								className={cn(
									"h-auto w-full justify-start gap-2 rounded-sm px-2 py-1.5 text-xs font-normal",
									"hover:bg-accent hover:text-accent-foreground [&_svg]:size-3.5",
									isActive && "bg-accent text-accent-foreground",
								)}
							>
								<Icon className="text-muted-foreground" />
								<span className="flex-1 text-left">{def.label}</span>
								{dottedFields.has(def.key) && (
									<span className="size-1.5 rounded-full bg-ring" />
								)}
								<ChevronRight className="text-muted-foreground" />
							</Button>
						);
					})
				)}
			</div>
			{canReset && (
				<>
					<div className="-mx-2 my-1 border-t border-border" />
					<Button
						variant="ghost"
						size="sm"
						onClick={onReset}
						className="h-auto w-full justify-start gap-2 rounded-sm px-2 py-1.5 text-xs font-normal text-muted-foreground hover:bg-accent hover:text-accent-foreground"
					>
						Reset filters
					</Button>
				</>
			)}
		</div>
	);
}
